import { useEffect, useState, useCallback } from "react";
import { Search, FilePlus2, FileText, Download, X, Loader2, Trash2, Plus, ExternalLink, Pencil } from "lucide-react";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";

const CATEGORIES = [
  "Omicidio",
  "Rapina a mano armata",
  "Furto",
  "Traffico di droga",
  "Traffico di armi",
  "Estorsione",
  "Rapimento",
  "Frode",
  "Sequestro di persona",
  "Aggressione",
  "Corruzione",
  "Riciclaggio",
  "Cybercrime",
  "Altro",
];

const SUBJECT_ROLES = [
  { value: "vittima", label: "Vittima" },
  { value: "sospetto", label: "Sospetto" },
  { value: "testimone", label: "Testimone" },
  { value: "informatore", label: "Informatore" },
  { value: "altro", label: "Altro" },
];

export default function AreaDetectiveBureau() {
  const { user } = useAuth();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [reportDialog, setReportDialog] = useState(null); // null | { mode: "new" } | { mode: "edit", report }
  const [viewDialog, setViewDialog] = useState(null); // report
  const [confirmDelete, setConfirmDelete] = useState(null); // report
  const [deleting, setDeleting] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/detective-bureau/reports");
      setReports(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore caricamento fascicoli");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const filtered = reports.filter((r) => {
    const q = filter.trim().toLowerCase();
    if (!q) return true;
    return (
      (r.case_number || "").toLowerCase().includes(q) ||
      (r.category || "").toLowerCase().includes(q) ||
      (r.redacted_by || "").toLowerCase().includes(q) ||
      (r.description || "").toLowerCase().includes(q)
    );
  });

  const downloadPdf = async (report) => {
    try {
      const res = await api.get(`/detective-bureau/reports/${report.id}/pdf`, {
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${report.case_number}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("Errore download PDF");
    }
  };

  const doDelete = async () => {
    if (!confirmDelete) return;
    setDeleting(true);
    try {
      const { data } = await api.delete(`/detective-bureau/reports/${confirmDelete.id}`);
      if (data?.discord_notice_ok) {
        toast.success(`Fascicolo ${data.case_number} eliminato. Avviso postato nel thread Discord.`);
      } else {
        toast.warning(`Fascicolo ${data.case_number} eliminato. ⚠️ Impossibile notificare Discord.`);
      }
      setConfirmDelete(null);
      setViewDialog(null);
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore eliminazione");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="detective-bureau-page">
      {/* HERO */}
      <div className="relative overflow-hidden rounded-2xl border border-sheriff-gold/30 bg-gradient-to-br from-[#1A1410] via-[#0F0A08] to-[#0A0605] p-6 sm:p-8">
        <div className="absolute -right-8 -bottom-8 h-40 w-40 rounded-full bg-sheriff-gold/10 blur-3xl" />
        <div className="relative flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-start gap-4 min-w-0">
            <div className="h-14 w-14 rounded-2xl bg-sheriff-gold/15 border border-sheriff-gold/40 flex items-center justify-center shrink-0">
              <Search className="h-7 w-7 text-sheriff-gold" />
            </div>
            <div className="min-w-0">
              <div className="uppercase tracking-[0.3em] text-[10px] text-sheriff-gold/80 font-mono">§ Detective Bureau</div>
              <h1 className="text-2xl sm:text-3xl font-heading font-black text-[#F5E6B8] mt-1">Fascicoli Investigativi</h1>
              <p className="text-sm text-[#D4C480]/80 mt-1 max-w-2xl">
                Redazione e archiviazione dei rapporti investigativi. Ogni fascicolo genera un PDF ufficiale
                e apre un nuovo thread nel forum Discord dedicato.
              </p>
            </div>
          </div>
          <Button
            onClick={() => setReportDialog({ mode: "new" })}
            className="bg-sheriff-gold hover:bg-sheriff-gold/90 text-[#0A1F18] font-bold shrink-0"
            data-testid="new-report-btn"
          >
            <FilePlus2 className="h-4 w-4 mr-2" /> Nuovo Fascicolo
          </Button>
        </div>
      </div>

      {/* SEARCH */}
      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-md">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-sheriff-gold/60" />
          <Input
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Cerca per numero, tipologia, redattore…"
            className="pl-9 bg-[#0F2620] border-sheriff-gold/30 text-[#F5E6B8]"
            data-testid="report-search"
          />
        </div>
        <div className="text-xs text-[#8B7B49] uppercase tracking-wider">
          {filtered.length} / {reports.length} fascicol{reports.length === 1 ? "o" : "i"}
        </div>
      </div>

      {/* LIST */}
      {loading ? (
        <div className="flex items-center gap-2 text-[#D4C480] py-12 justify-center">
          <Loader2 className="h-4 w-4 animate-spin" /> Caricamento fascicoli…
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center text-[#8B7B49] py-16 border border-dashed border-sheriff-gold/20 rounded-2xl">
          <FileText className="h-10 w-10 mx-auto text-sheriff-gold/40 mb-2" />
          <div className="uppercase tracking-wider text-xs">Nessun fascicolo archiviato</div>
          <div className="text-sm mt-1">Premi &ldquo;Nuovo Fascicolo&rdquo; per crearne uno.</div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {filtered.map((r) => (
            <ReportCard
              key={r.id}
              report={r}
              onView={() => setViewDialog(r)}
              onDownload={() => downloadPdf(r)}
              onEdit={() => setReportDialog({ mode: "edit", report: r })}
              onDelete={() => setConfirmDelete(r)}
            />
          ))}
        </div>
      )}

      {/* NEW / EDIT REPORT DIALOG */}
      <NewReportDialog
        open={!!reportDialog}
        mode={reportDialog?.mode}
        report={reportDialog?.report}
        onClose={() => setReportDialog(null)}
        onSaved={() => { setReportDialog(null); reload(); }}
      />

      {/* VIEW DETAIL DIALOG */}
      <ReportDetailDialog
        report={viewDialog}
        onClose={() => setViewDialog(null)}
        onDownload={downloadPdf}
        onEdit={(r) => { setViewDialog(null); setReportDialog({ mode: "edit", report: r }); }}
        onDelete={(r) => setConfirmDelete(r)}
      />

      {/* CONFIRM DELETE */}
      <Dialog open={!!confirmDelete} onOpenChange={(o) => !o && !deleting && setConfirmDelete(null)}>
        <DialogContent className="bg-[#0A1F18] border-rose-500/40 max-w-md" data-testid="confirm-delete-dialog">
          <DialogHeader>
            <DialogTitle className="text-rose-200 font-heading flex items-center gap-2">
              <Trash2 className="h-5 w-5" /> Elimina Fascicolo
            </DialogTitle>
          </DialogHeader>
          <div className="text-sm text-[#D4C480] space-y-2 py-2">
            <div>Confermi l&apos;eliminazione del fascicolo <span className="font-mono text-sheriff-gold">{confirmDelete?.case_number}</span>?</div>
            <div className="text-xs text-[#8B7B49] italic">Il fascicolo verrà rimosso dal portale. Il thread Discord resterà come archivio storico ma sarà marcato come &quot;eliminato&quot;.</div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setConfirmDelete(null)} disabled={deleting} className="text-[#D4C480]">Annulla</Button>
            <Button
              onClick={doDelete}
              disabled={deleting}
              className="bg-rose-500 hover:bg-rose-600 text-white font-bold"
              data-testid="confirm-delete-btn"
            >
              {deleting ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Eliminazione…</> : <><Trash2 className="h-4 w-4 mr-2" /> Elimina definitivamente</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


function ReportCard({ report, onView, onDownload, onEdit, onDelete }) {
  const created = report.created_at ? new Date(report.created_at) : null;
  const n_subj = (report.subjects || []).length;
  return (
    <div
      className="rounded-2xl border border-sheriff-gold/25 bg-[#0F2620] hover:border-sheriff-gold/50 transition-all p-4 flex flex-col gap-3"
      data-testid={`report-card-${report.id}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-sheriff-gold/80">{report.case_number}</div>
          <div className="text-sm font-heading font-bold text-[#F5E6B8] mt-1 truncate">{report.category || "—"}</div>
        </div>
        <Badge className="bg-sheriff-gold/15 text-sheriff-gold border-sheriff-gold/40 text-[10px] uppercase tracking-wider shrink-0">
          {n_subj} sogg.
        </Badge>
      </div>
      <div className="text-xs text-[#D4C480]/80 line-clamp-2">
        {report.description || "—"}
      </div>
      <div className="flex items-center justify-between text-[11px] text-[#8B7B49] mt-auto pt-2 border-t border-sheriff-gold/10">
        <div className="truncate">
          <span className="uppercase tracking-wider">Redatto da:</span> <span className="text-[#D4C480]">{report.redacted_by}</span>
        </div>
        <div>{created ? created.toLocaleDateString("it-IT") : "—"}</div>
      </div>
      <div className="grid grid-cols-4 gap-1">
        <Button
          onClick={onView}
          variant="ghost"
          size="sm"
          className="text-sheriff-gold hover:bg-sheriff-gold/10 text-xs px-2"
          data-testid={`report-view-${report.id}`}
        >
          <FileText className="h-3.5 w-3.5" />
        </Button>
        <Button
          onClick={onDownload}
          variant="ghost"
          size="sm"
          className="text-[#F5E6B8] hover:bg-sheriff-gold/10 text-xs px-2"
          data-testid={`report-download-${report.id}`}
        >
          <Download className="h-3.5 w-3.5" />
        </Button>
        <Button
          onClick={onEdit}
          variant="ghost"
          size="sm"
          className="text-amber-300 hover:bg-amber-400/10 text-xs px-2"
          data-testid={`report-edit-${report.id}`}
          title="Modifica"
        >
          <Pencil className="h-3.5 w-3.5" />
        </Button>
        <Button
          onClick={onDelete}
          variant="ghost"
          size="sm"
          className="text-rose-300 hover:bg-rose-400/10 text-xs px-2"
          data-testid={`report-delete-${report.id}`}
          title="Elimina"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      </div>
    </div>
  );
}


function NewReportDialog({ open, mode, report, onClose, onSaved }) {
  const isEdit = mode === "edit" && !!report;
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    incident_datetime: "",
    category: "",
    officers_involved: "",
    description: "",
    evidence: "",
    conclusions: "",
  });
  const [subjects, setSubjects] = useState([{ id: crypto.randomUUID(), name: "", role: "sospetto" }]);

  useEffect(() => {
    if (!open) return;
    if (isEdit) {
      setForm({
        incident_datetime: report.incident_datetime || "",
        category: report.category || "",
        officers_involved: report.officers_involved || "",
        description: report.description || "",
        evidence: report.evidence || "",
        conclusions: report.conclusions || "",
      });
      const subs = (report.subjects || []).map((s) => ({ id: crypto.randomUUID(), name: s.name || "", role: s.role || "sospetto" }));
      setSubjects(subs.length ? subs : [{ id: crypto.randomUUID(), name: "", role: "sospetto" }]);
    } else {
      setForm({
        incident_datetime: "",
        category: "",
        officers_involved: "",
        description: "",
        evidence: "",
        conclusions: "",
      });
      setSubjects([{ id: crypto.randomUUID(), name: "", role: "sospetto" }]);
    }
  }, [open, isEdit, report]);

  const addSubject = () => setSubjects((s) => [...s, { id: crypto.randomUUID(), name: "", role: "sospetto" }]);
  const removeSubject = (idx) => setSubjects((s) => s.filter((_, i) => i !== idx));
  const updateSubject = (idx, field, value) =>
    setSubjects((s) => s.map((x, i) => (i === idx ? { ...x, [field]: value } : x)));

  const submit = async () => {
    if (!form.incident_datetime.trim() || !form.category.trim() || form.description.trim().length < 10) {
      toast.error("Compila almeno data, tipologia e descrizione (min 10 caratteri).");
      return;
    }
    const cleanSubjects = subjects
      .filter((s) => s.name.trim())
      .map(({ id: _id, ...rest }) => rest);
    setSaving(true);
    try {
      const payload = {
        incident_datetime: form.incident_datetime.trim(),
        category: form.category.trim(),
        officers_involved: form.officers_involved.trim(),
        subjects: cleanSubjects,
        description: form.description.trim(),
        evidence: form.evidence.trim(),
        conclusions: form.conclusions.trim(),
      };
      let data;
      if (isEdit) {
        const res = await api.patch(`/detective-bureau/reports/${report.id}`, payload);
        data = res.data;
        if (data?.discord_update_ok) {
          toast.success(`Fascicolo ${data.case_number} aggiornato. PDF v2 inviato nel thread Discord.`);
        } else {
          toast.warning(`Fascicolo ${data.case_number} aggiornato. ⚠️ Errore invio PDF aggiornato a Discord.`);
        }
      } else {
        const res = await api.post("/detective-bureau/reports", payload);
        data = res.data;
        if (data?.discord_ok) {
          toast.success(`Fascicolo ${data.case_number} creato e postato su Discord.`);
        } else {
          toast.warning(`Fascicolo ${data.case_number} salvato. ⚠️ Errore invio Discord (verifica webhook).`);
        }
      }
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore salvataggio fascicolo");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className="bg-[#0A1F18] border-sheriff-gold/40 max-w-3xl max-h-[90vh] overflow-y-auto"
        data-testid={isEdit ? "edit-report-dialog" : "new-report-dialog"}
      >
        <DialogHeader>
          <DialogTitle className="text-[#F5E6B8] font-heading flex items-center gap-2">
            {isEdit ? <Pencil className="h-5 w-5 text-amber-300" /> : <FilePlus2 className="h-5 w-5 text-sheriff-gold" />}
            {isEdit ? `Modifica Fascicolo ${report?.case_number}` : "Nuovo Fascicolo Investigativo"}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Data e ora dell'evento *">
              <Input
                value={form.incident_datetime}
                onChange={(e) => setForm({ ...form, incident_datetime: e.target.value })}
                placeholder="es. 03/07/2026 22:30"
                className="bg-[#0F2620] border-sheriff-gold/30 text-[#F5E6B8]"
                data-testid="report-incident-datetime"
              />
            </Field>
            <Field label="Tipologia *">
              <select
                value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
                className="w-full bg-[#0F2620] border border-sheriff-gold/30 rounded-md px-3 py-2 text-sm text-[#F5E6B8] focus:outline-none focus:border-sheriff-gold"
                data-testid="report-category"
              >
                <option value="">— Seleziona tipologia —</option>
                {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </Field>
          </div>
          <Field label="Ufficiali coinvolti">
            <Input
              value={form.officers_involved}
              onChange={(e) => setForm({ ...form, officers_involved: e.target.value })}
              placeholder="es. Det. Rossi, Sgt. Verdi"
              className="bg-[#0F2620] border-sheriff-gold/30 text-[#F5E6B8]"
              data-testid="report-officers"
            />
          </Field>

          {/* SOGGETTI */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block uppercase-label text-[10px] text-sheriff-gold tracking-wider">Soggetti coinvolti</label>
              <Button
                type="button"
                onClick={addSubject}
                variant="ghost"
                size="sm"
                className="text-sheriff-gold hover:bg-sheriff-gold/10 text-xs h-7"
                data-testid="add-subject-btn"
              >
                <Plus className="h-3.5 w-3.5 mr-1" /> Aggiungi
              </Button>
            </div>
            <div className="space-y-2">
              {subjects.map((s, idx) => (
                <div key={s.id || `subject-${idx}`} className="flex gap-2 items-center" data-testid={`subject-row-${idx}`}>
                  <Input
                    value={s.name}
                    onChange={(e) => updateSubject(idx, "name", e.target.value)}
                    placeholder="Nome del soggetto"
                    className="flex-1 bg-[#0F2620] border-sheriff-gold/30 text-[#F5E6B8]"
                  />
                  <select
                    value={s.role}
                    onChange={(e) => updateSubject(idx, "role", e.target.value)}
                    className="bg-[#0F2620] border border-sheriff-gold/30 rounded-md px-2 py-2 text-sm text-[#F5E6B8] focus:outline-none focus:border-sheriff-gold w-36"
                  >
                    {SUBJECT_ROLES.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
                  </select>
                  {subjects.length > 1 && (
                    <Button
                      type="button"
                      onClick={() => removeSubject(idx)}
                      variant="ghost"
                      size="sm"
                      className="text-rose-300 hover:bg-rose-400/10 shrink-0"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  )}
                </div>
              ))}
            </div>
          </div>

          <Field label="Descrizione dei fatti * (min 10 caratteri)">
            <Textarea
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              placeholder="Racconto dettagliato dell'evento, dinamiche, persone coinvolte…"
              rows={5}
              className="bg-[#0F2620] border-sheriff-gold/30 text-[#F5E6B8]"
              data-testid="report-description"
            />
          </Field>
          <Field label="Prove raccolte">
            <Textarea
              value={form.evidence}
              onChange={(e) => setForm({ ...form, evidence: e.target.value })}
              placeholder="CCTV, impronte digitali, testimonianze, oggetti trovati…"
              rows={3}
              className="bg-[#0F2620] border-sheriff-gold/30 text-[#F5E6B8]"
              data-testid="report-evidence"
            />
          </Field>
          <Field label="Conclusioni / Azioni intraprese">
            <Textarea
              value={form.conclusions}
              onChange={(e) => setForm({ ...form, conclusions: e.target.value })}
              placeholder="Mandato di cattura, arresto, caso archiviato, indagini aperte…"
              rows={3}
              className="bg-[#0F2620] border-sheriff-gold/30 text-[#F5E6B8]"
              data-testid="report-conclusions"
            />
          </Field>
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose} disabled={saving} className="text-[#D4C480]">
            Annulla
          </Button>
          <Button
            onClick={submit}
            disabled={saving}
            className="bg-sheriff-gold hover:bg-sheriff-gold/90 text-[#0A1F18] font-bold"
            data-testid="submit-report-btn"
          >
            {saving ? (
              <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Salvataggio…</>
            ) : isEdit ? (
              <><Pencil className="h-4 w-4 mr-2" /> Aggiorna e Reinvia PDF</>
            ) : (
              <><FilePlus2 className="h-4 w-4 mr-2" /> Salva e Invia a Discord</>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function ReportDetailDialog({ report, onClose, onDownload, onEdit, onDelete }) {
  if (!report) return null;
  const created = report.created_at ? new Date(report.created_at) : null;
  return (
    <Dialog open={!!report} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        className="bg-[#0A1F18] border-sheriff-gold/40 max-w-3xl max-h-[90vh] overflow-y-auto"
        data-testid="report-detail-dialog"
      >
        <DialogHeader>
          <DialogTitle className="text-[#F5E6B8] font-heading flex items-center gap-2">
            <FileText className="h-5 w-5 text-sheriff-gold" /> Fascicolo {report.case_number}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-2 text-sm">
          <div className="grid grid-cols-2 gap-3 text-xs">
            <DetailField label="Numero" value={report.case_number} mono />
            <DetailField label="Tipologia" value={report.category} />
            <DetailField label="Data evento" value={report.incident_datetime} />
            <DetailField label="Creato il" value={created ? created.toLocaleString("it-IT") : "—"} />
            <DetailField label="Redatto da" value={`${report.redacted_by || "—"} (${report.redacted_by_role || "—"})`} />
            <DetailField label="Ufficiali coinvolti" value={report.officers_involved || "—"} />
          </div>

          <div>
            <div className="text-[10px] uppercase tracking-wider text-sheriff-gold/80 mb-1">Soggetti</div>
            {report.subjects?.length ? (
              <div className="space-y-1">
                {report.subjects.map((s, i) => (
                  <div key={`${s.name}-${s.role}-${i}`} className="flex items-center gap-2 p-2 rounded bg-[#0F2620] border border-sheriff-gold/15">
                    <span className="text-[#F5E6B8]">{s.name}</span>
                    <Badge className="bg-sheriff-gold/15 text-sheriff-gold border-sheriff-gold/40 text-[10px] uppercase">{s.role}</Badge>
                  </div>
                ))}
              </div>
            ) : <div className="text-[#8B7B49] italic">Nessuno</div>}
          </div>

          <DetailBlock label="Descrizione" value={report.description} />
          <DetailBlock label="Prove" value={report.evidence} />
          <DetailBlock label="Conclusioni" value={report.conclusions} />
        </div>
        <DialogFooter className="gap-2 flex-wrap">
          {report.discord_thread_id && (
            <a
              href={`https://discord.com/channels/@me/${report.discord_thread_id}`}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1.5 text-xs text-sheriff-gold hover:text-[#F5E6B8]"
            >
              <ExternalLink className="h-3.5 w-3.5" /> Apri thread Discord
            </a>
          )}
          <div className="flex-1" />
          {onDelete && (
            <Button
              onClick={() => onDelete(report)}
              variant="ghost"
              className="text-rose-300 hover:bg-rose-400/10"
              data-testid="detail-delete-btn"
            >
              <Trash2 className="h-4 w-4 mr-2" /> Elimina
            </Button>
          )}
          {onEdit && (
            <Button
              onClick={() => onEdit(report)}
              variant="ghost"
              className="text-amber-300 hover:bg-amber-400/10"
              data-testid="detail-edit-btn"
            >
              <Pencil className="h-4 w-4 mr-2" /> Modifica
            </Button>
          )}
          <Button variant="ghost" onClick={onClose} className="text-[#D4C480]">Chiudi</Button>
          <Button
            onClick={() => onDownload(report)}
            className="bg-sheriff-gold hover:bg-sheriff-gold/90 text-[#0A1F18] font-bold"
            data-testid="detail-download-pdf"
          >
            <Download className="h-4 w-4 mr-2" /> Scarica PDF
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function Field({ label, children }) {
  return (
    <div>
      <label className="block uppercase-label text-[10px] text-sheriff-gold tracking-wider mb-1.5">{label}</label>
      {children}
    </div>
  );
}


function DetailField({ label, value, mono }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-sheriff-gold/80">{label}</div>
      <div className={`text-[#F5E6B8] ${mono ? "font-mono" : ""}`}>{value || "—"}</div>
    </div>
  );
}


function DetailBlock({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider text-sheriff-gold/80 mb-1">{label}</div>
      <div className="text-[#F5E6B8] whitespace-pre-wrap bg-[#0F2620] border border-sheriff-gold/15 rounded-lg p-3">
        {value || "—"}
      </div>
    </div>
  );
}
